import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

/**
 * CookieConsent - Minimal, respectful cookie banner
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 */

type ConsentKeys = 'essential' | 'functional' | 'analytics'

type ConsentState = {
  version: string
  updatedAt: string
  choices: Record<ConsentKeys, boolean>
}

const CONSENT_VERSION = '1.0.0'
const STORAGE_KEY = 'lukhas_id_consent_v1'

const defaultChoices: ConsentState['choices'] = {
  essential: true,
  functional: false,
  analytics: false,
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false)
  const [showPrefs, setShowPrefs] = useState(false)
  const [choices, setChoices] = useState<ConsentState['choices']>(defaultChoices)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      const saved = raw ? (JSON.parse(raw) as ConsentState) : null
      const needsBanner = !saved || saved.version !== CONSENT_VERSION

      if (needsBanner) {
        const timer = setTimeout(() => setOpen(true), 1000)
        setChoices(saved?.choices ?? defaultChoices)
        return () => clearTimeout(timer)
      }
    } catch {
      setOpen(true)
    }
  }, [])

  function persist(nextChoices: ConsentState['choices']) {
    const record: ConsentState = {
      version: CONSENT_VERSION,
      updatedAt: new Date().toISOString(),
      choices: nextChoices,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
    setOpen(false)
  }

  function acceptAll() {
    persist({ essential: true, functional: true, analytics: true })
  }

  function acceptEssential() {
    persist(defaultChoices)
  }

  function acceptSelected() {
    persist(choices)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          role="dialog"
          aria-modal="false"
          aria-labelledby="lukhas-cookie-title"
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="max-w-5xl mx-auto px-6 py-4">
            <AnimatePresence mode="wait">
              {!showPrefs ? (
                <motion.div
                  key="compact"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col sm:flex-row gap-4 items-start sm:items-center"
                >
                  {/* Message */}
                  <p className="text-sm text-white/50 font-light leading-relaxed flex-1">
                    <span className="text-white/70">Your data, your call.</span>{' '}
                    We use essential cookies by default.{' '}
                    <Link to="/privacy" className="text-white/70 hover:text-white transition-colors underline-offset-2 hover:underline">
                      Privacy
                    </Link>
                    {' · '}
                    <Link to="/cookies" className="text-white/70 hover:text-white transition-colors underline-offset-2 hover:underline">
                      Cookies
                    </Link>
                  </p>

                  {/* Actions */}
                  <div className="flex gap-3 items-center shrink-0">
                    <button
                      onClick={() => setShowPrefs(true)}
                      className="
                        px-4 py-2 text-xs font-light tracking-wide
                        text-white/40 hover:text-white/70
                        transition-colors duration-300
                      "
                    >
                      Preferences
                    </button>
                    <button
                      onClick={acceptEssential}
                      className="
                        px-4 py-2 text-xs font-light tracking-wide
                        border border-white/20 text-white/60
                        hover:border-white/40 hover:text-white
                        transition-colors duration-300
                      "
                    >
                      Essential only
                    </button>
                    <button
                      onClick={acceptAll}
                      className="
                        px-4 py-2 text-xs font-medium tracking-wide
                        bg-white text-black
                        hover:bg-white/90
                        transition-colors duration-300
                      "
                    >
                      Accept all
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="expanded"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-light text-white tracking-wide">
                      Cookie Preferences
                    </h3>
                    <button
                      onClick={() => setShowPrefs(false)}
                      className="p-1 text-white/30 hover:text-white/60 transition-colors"
                      aria-label="Close preferences"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
                    <PrefRow
                      id="essential"
                      label="Essential"
                      desc="Required for security and core functionality. Always on."
                      checked={true}
                      disabled={true}
                      onChange={() => {}}
                    />
                    <PrefRow
                      id="functional"
                      label="Functional"
                      desc="Remembers your settings and preferences."
                      checked={choices.functional}
                      onChange={(v) => setChoices((c) => ({ ...c, functional: v }))}
                    />
                    <PrefRow
                      id="analytics"
                      label="Analytics"
                      desc="Privacy-preserving usage metrics."
                      checked={choices.analytics}
                      onChange={(v) => setChoices((c) => ({ ...c, analytics: v }))}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      onClick={acceptEssential}
                      className="
                        px-4 py-2 text-xs font-light tracking-wide
                        border border-white/20 text-white/60
                        hover:border-white/40 hover:text-white
                        transition-colors duration-300
                      "
                    >
                      Essential only
                    </button>
                    <button
                      onClick={acceptSelected}
                      className="
                        px-4 py-2 text-xs font-medium tracking-wide
                        bg-white text-black
                        hover:bg-white/90
                        transition-colors duration-300
                      "
                    >
                      Save preferences
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function PrefRow({
  id,
  label,
  desc,
  checked,
  disabled,
  onChange,
}: {
  id: string
  label: string
  desc: string
  checked?: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  const inputId = `lukhas-cookie-${id}`

  return (
    <div className="bg-black p-4 space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="text-sm font-light text-white/80">
          {label}
        </label>
        <button
          id={inputId}
          role="switch"
          aria-checked={!!checked}
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          className={`
            relative inline-flex h-5 w-9 items-center transition-colors duration-300
            ${checked ? 'bg-white' : 'bg-white/20'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              inline-block h-3 w-3 transform transition-transform duration-300
              ${checked ? 'translate-x-5 bg-black' : 'translate-x-1 bg-white/60'}
            `}
          />
          <span className="sr-only">Toggle {label}</span>
        </button>
      </div>
      <p className="text-xs text-white/40 font-light leading-relaxed">
        {desc}
      </p>
    </div>
  )
}
